/**
 * Colocación de paneles desplegables
 * -----------------------------------------------------------------------------
 * El panel se monta en `document.body` con `position: fixed` y se coloca a mano
 * respecto al campo, igual que hace MDB con sus dropdowns (`container: 'body'`).
 *
 * Por qué no dejarlo dentro del campo con `position: absolute`: anidado en un
 * `.input-group` la caja del panel no llegaba a envolver a su lista, así que no
 * pintaba el fondo detrás de las opciones y además medía casi cero de alto, con
 * lo que la decisión de abrir arriba o abajo salía siempre mal. Sacándolo del
 * flujo del formulario desaparecen los dos problemas de golpe.
 *
 * La colocación evita además que el panel se salga de la pantalla: si no cabe
 * por debajo del campo se abre hacia arriba, y su alto se limita al hueco real
 * disponible para que la lista pueda hacer scroll dentro.
 */

const GAP = 4;
const MIN_SPACE = 120; // por debajo de esto un panel no es usable
// Tope de alto. Sin él, en una pantalla alta el panel se estira hasta ocupar
// casi todo el viewport: la lista de países entera son más de 2.000 px.
const MAX_HEIGHT = 320;

function clampHeight(space, max) {
  return Math.max(Math.min(Math.round(space), max), MIN_SPACE);
}

/**
 * @param {HTMLElement} anchor Campo de referencia.
 * @param {HTMLElement} panel  Panel ya insertado en el body.
 * @param {number} [maxHeight] Alto máximo en píxeles.
 * @returns {'up'|'down'} Hacia dónde se abrió.
 */
export function placePanel(anchor, panel, maxHeight = MAX_HEIGHT) {
  if (!anchor || !panel) return 'down';

  const rect = anchor.getBoundingClientRect();
  const viewport = window.innerHeight || document.documentElement.clientHeight;

  panel.style.position = 'fixed';
  panel.style.left = `${Math.round(rect.left)}px`;
  panel.style.minWidth = `${Math.round(rect.width)}px`;

  // Se mide sin límite de alto para saber cuánto pide realmente.
  panel.style.maxHeight = '';
  panel.style.top = '0px';
  panel.style.bottom = 'auto';
  const needed = panel.offsetHeight;

  const spaceBelow = viewport - rect.bottom - GAP;
  const spaceAbove = rect.top - GAP;

  // Lo que el panel necesita de verdad nunca pasa del tope: si cabe eso, se
  // abre hacia abajo aunque el contenido sin recortar fuese enorme.
  const wanted = Math.min(needed, maxHeight);
  const openDown = wanted <= spaceBelow || spaceBelow >= Math.max(spaceAbove, MIN_SPACE);

  if (openDown) {
    panel.style.top = `${Math.round(rect.bottom + GAP)}px`;
    panel.style.bottom = 'auto';
    panel.style.maxHeight = `${clampHeight(spaceBelow, maxHeight)}px`;
  } else {
    panel.style.top = 'auto';
    panel.style.bottom = `${Math.round(viewport - rect.top + GAP)}px`;
    panel.style.maxHeight = `${clampHeight(spaceAbove, maxHeight)}px`;
  }

  return openDown ? 'down' : 'up';
}

/**
 * Cierra el panel si la página se desplaza o cambia de tamaño: al estar fijado
 * al viewport, quedaría flotando lejos de su campo.
 *
 * El scroll se escucha en fase de captura para enterarse también del de
 * contenedores internos de la página, así que hay que descartar expresamente el
 * que ocurre DENTRO del panel: si no, mover la lista de países la cerraría.
 *
 * @param {Function} close Cierre del panel.
 * @param {HTMLElement} panel Panel cuyo scroll propio debe ignorarse.
 * @returns {Function} Función para dejar de vigilar.
 */
export function closeOnViewportChange(close, panel) {
  const onScroll = (event) => {
    // `event.target` puede ser `window` o `document`, que no son nodos
    // contenidos en el panel: hay que comprobar `nodeType` antes de usar
    // `contains`, o la llamada falla y el panel nunca se cierra.
    const target = event.target;
    if (panel && target && target.nodeType === 1 && panel.contains(target)) return;
    close();
  };
  const onResize = () => close();

  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onResize);

  return () => {
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onResize);
  };
}

export default placePanel;
